import { useEffect, useRef, useState } from "react";
import { createStore } from "zustand/vanilla";
import { unifiedProjectSocketManager } from "../../../../../socket";

// ═════════════════════════════════════════════════════════════════════════════
// Execution Blueprint — Live Review socket store
//
// ⚠️ THIS FILE IS INTENTIONALLY IDENTICAL IN TWO REPOSITORIES.
//
//   10x-coach-frontend/src/components/CoachPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_socket/LiveReviewSocketStore.js
//   10x-frontend/src/components/UserPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_socket/LiveReviewSocketStore.js
//
// Live Review is the first screen in this product where two different
// logged-in people — a CEO in one app, their coach in another — join the same
// room and see each other's changes. Both apps therefore receive the SAME
// `live_review_details` snapshot and the same four delta events, and any
// disagreement between their two readers is a bug that shows up as the two
// people seeing different things while sitting in a meeting together. That is
// close to the worst possible place for a subtle drift.
//
// There is no shared package between the two repos, so the defence is that
// these files are byte-for-byte the same and `diff` is the test. Both are
// placed at the same folder depth precisely so the relative import above is
// identical too. **Any change here must be applied to both copies in the same
// pass.** If you find yourself wanting an app-specific branch, pass it in as an
// argument instead — `viewerRole` on `connect()` is the existing example, and
// deliberately the only one.
//
// Contract: `12-live-review-fe-handoff.md` (§5 connecting, §6 events, §8 error
// codes). Backend is implemented and code-verified; the coach and CEO screens
// are what was missing.
// ═════════════════════════════════════════════════════════════════════════════

// The namespace key registered in both repos' `src/socket.js` manager switches.
// Not the URL — the manager maps this to the right `io()` instance.
export const LIVE_REVIEW_NAMESPACE = "live-review";

// ─── Wire vocabulary (§6, §6a) ───────────────────────────────────────────────
// Client → server.
export const LIVE_REVIEW_EMIT = {
  // Always first. §5: "Nothing else works until this succeeds."
  JOIN: "join_live_review",
  // { execution_blueprint_item_id, value } — overwrites immediately, no confirm
  // step and no save button (§3.4). Rejected with `wigs_locked` once locked.
  EDIT_ITEM: "edit_live_review_item",
  // { execution_blueprint_item_id, status } — either party, not coach-only.
  SET_ITEM_STATUS: "set_item_review_status",
  // ── Item add / delete (handoff #25 §2, 2026-09-14) ────────────────────────
  // { section_key, label, value, rationale? }. Rejected with `section_not_found`
  // for an unknown section, `item_limit_exceeded` once the section is full.
  ADD_ITEM: "add_live_review_item",
  // { execution_blueprint_item_id }. A SOFT delete — the row is flagged, not
  // removed — and every connector touching either end of it is cascade-deleted
  // in the same transaction. Rejected with `item_not_found`, or
  // `item_floor_reached` when it would take the section below one item.
  DELETE_ITEM: "delete_live_review_item",

  // ── Item reordering (handoff #25 §3, 2026-09-14) ──────────────────────────
  // { section_key, ordered_item_ids } — the section's FULL new order, every
  // current non-deleted item exactly once. A move delta is not accepted;
  // anything missing, extra or duplicated comes back as `invalid_reorder`.
  //
  // ⚠️ Valid for the WHOLE of Live Review, not just the finalize step. #25 §2
  // widened add/edit/delete/reorder to run until the terminal lock, so unlike
  // the connector emits below this one is NOT gated on `live_review_step`.
  REORDER_SECTION_ITEMS: "reorder_section_items",
  // { remark_type, id } — one-way, and `id` is the REMARK's id (see below).
  MARK_REMARK_DISCUSSED: "mark_remark_discussed",
  // No payload. ⚠️ As of 2026-08-27 (§6a) this GENUINELY LOCKS: it permanently
  // freezes every item value and kicks off the connectors agent. There is no
  // undo. It is only offered once `remarks_remaining === 0`, and it must sit
  // behind a confirmation that says so.
  SUBMIT_FINALIZE: "submit_finalize_blueprint",

  // ── Connector editing (§6a) — valid ONLY during `connectors_review` ───────
  // Outside that step the server answers `wrong_step`. `*_item_id` is omitted
  // (or null) exactly when the matching `*_node_type` is "bhag".
  ADD_CONNECTOR: "add_connector",
  // In-place edit: the connector's `id` and `source` do NOT change, only its
  // endpoints. So a reducer should patch the edge, never replace-and-append.
  UPDATE_CONNECTOR: "update_connector",
  // Soft delete. Either party, any connector — AI-suggested or manual. There is
  // deliberately no "only delete your own" rule.
  DELETE_CONNECTOR: "delete_connector",

  // ── Step 3 (§6b) ─────────────────────────────────────────────────────────
  // No payload. Advances past `connectors_review` and starts the Year 1
  // shortlist agent. ⚠️ §6b.1: "no completeness gate — don't require any
  // minimum number of connectors before enabling the button", and submitting
  // the AI draft with zero manual edits is explicitly fine.
  CONFIRM_CONNECTIONS: "confirm_connections",
  // { execution_blueprint_item_id, is_year1_shortlisted } — only during
  // `year1_review`.
  SET_ITEM_SHORTLIST: "set_item_shortlist",
  // ⚠️ BREAKING SHAPE CHANGE (handoff #25 §4, 2026-09-14):
  // `{ execution_blueprint_item_id, owner_user_ids: number[] }` — `[]`
  // unassigns everyone. It was `owner_user_id: number | null`, and the two are
  // NOT compatible, so this needs a coordinated deploy with the backend.
  //
  // Send the full new list every time, not a delta. Ids are validated against
  // the company's active roster and the WHOLE call is rejected with
  // `invalid_owner` (naming the bad ids) if any is unknown — there is no
  // partial write. Only during `year1_review`.
  SET_ITEM_OWNER: "set_item_owner",
  // ⚠️ No payload, and THE REAL TERMINAL LOCK — `blueprint_state` becomes
  // LOCKED and there is no undo. Distinct from `SUBMIT_FINALIZE` above, which
  // only freezes item VALUES; this ends the whole thing. §6b.7 is explicit that
  // there is no server-side "everything must be reviewed" gate, so **the
  // confirmation dialog in front of this is the only thing standing between a
  // stray click and a permanently locked blueprint.**
  CONFIRM_YEAR1_PLAN: "confirm_year1_plan",
};

// Server → client.
export const LIVE_REVIEW_ON = {
  // Once, to the joiner only. The whole screen's initial state.
  DETAILS: "live_review_details",
  ITEM_UPDATED: "item_updated",
  // { section_key, item_order, reordered_by, reordered_at } — `item_order` is
  // the section's full new id order. Broadcast to the room, including the
  // person who reordered, same as every other action here.
  SECTION_ITEMS_REORDERED: "section_items_reordered",
  // The whole new item, plus `section_key`, `item_order`, `added_by`, `added_at`.
  ITEM_ADDED: "item_added",
  // { execution_blueprint_item_id, section_key, deleted_connector_ids,
  //   deleted_by, deleted_at }.
  //
  // ⚠️ `deleted_connector_ids` IS THE POINT. Deleting a WIG cascade-deletes
  // every connector touching either of its ends, and this array is the only
  // notice of it — there is no separate `connector_deleted` per edge. Dropping
  // them here is what stops the map drawing lines to a card that is gone.
  ITEM_DELETED: "item_deleted",
  ITEM_STATUS_UPDATED: "item_status_updated",
  REMARKS_UPDATED: "remarks_updated",
  PRESENCE_UPDATE: "presence_update",

  // ⚠️ §6a, and the most important addition in that section: the step is what
  // moves both screens on, and it arrives as a BROADCAST rather than as a reply
  // to whoever clicked. It fires twice after a lock —
  // `{ live_review_step: "connectors_drafting", wigs_locked_at }` immediately,
  // then `{ live_review_step: "connectors_review", connectors }` when the agent
  // finishes, with no fixed delay between them.
  //
  // The consequence for the UI: **do not navigate on the click.** Navigating
  // locally would move only the person who pressed the button and leave the
  // other one on a screen that is no longer live. Route off the step instead
  // and both parties move together — including a party who was disconnected at
  // the time, because §6a.4 says a re-`join` returns the current step and "that
  // is what routes your screen, not which broadcast you happened to catch."
  STEP_CHANGED: "live_review_step_changed",

  // Connector mutations. Every one of these carries a freshly recomputed
  // `overview`, so the Connection Overview panel is updated straight from the
  // event and never recomputed locally or re-fetched (§6a).
  CONNECTOR_ADDED: "connector_added",
  CONNECTOR_UPDATED: "connector_updated",
  CONNECTOR_DELETED: "connector_deleted",

  // ── Step 3 (§6b) ─────────────────────────────────────────────────────────
  // `STEP_CHANGED` carries this stage too: `year1_drafting` immediately after
  // `confirm_connections`, then `year1_review` with `{ year1: { items, roster } }`.
  ITEM_SHORTLIST_UPDATED: "item_shortlist_updated",
  // ⚠️ Always carries `owner_suggested_by_agent: false`, even when the picked
  // owner IS the agent's own suggestion — §6b.6: "picking anyone via this action
  // always counts as a human decision". So the AI badge disappears on any
  // deliberate pick, which is the whole point of the flag.
  ITEM_OWNER_UPDATED: "item_owner_updated",
  // The terminal lock landing. Broadcast once, with no follow-up agent.
  BLUEPRINT_LOCKED: "blueprint_locked",

  // Only to whoever triggered the failed action; the other party never sees it.
  ERROR: "error",
};

// §6: "Every successful action is broadcast to both parties at once, including
// whoever performed it." So there is exactly one render path for "an edit
// happened" and no special-casing of our own actions — which is also why the
// appliers below never check whether we were the actor.

// §8. Codes are stable; copy is ours.
export const LIVE_REVIEW_ERROR_CODE = {
  NOT_AUTHORIZED: "not_authorized",
  LIVE_REVIEW_NOT_STARTED: "live_review_not_started",
  ITEM_NOT_FOUND: "item_not_found",
  WRONG_STEP: "wrong_step",
  REMARKS_REMAINING: "remarks_remaining",
  REMARK_NOT_FOUND: "remark_not_found",
  // §6a: an edit that arrived after the lock. The UI should make this
  // unreachable by removing every editing affordance once past
  // `finalize_blueprint` — if it fires, something is stale, so it resnapshots.
  WIGS_LOCKED: "wigs_locked",
  // Connector validation (§6a / §8).
  // ── Handoff #25 §2/§3 ────────────────────────────────────────────────────
  SECTION_NOT_FOUND: "section_not_found",
  ITEM_LIMIT_EXCEEDED: "item_limit_exceeded",
  ITEM_FLOOR_REACHED: "item_floor_reached",
  INVALID_REORDER: "invalid_reorder",

  INVALID_CONNECTOR: "invalid_connector",
  DUPLICATE_CONNECTOR: "duplicate_connector",
  CONNECTOR_NOT_FOUND: "connector_not_found",
  // §6b: an owner id that isn't an active user in this company.
  INVALID_OWNER: "invalid_owner",
  // §6b.7: any Year 1 mutation after the terminal lock has fired.
  BLUEPRINT_ALREADY_LOCKED: "blueprint_already_locked",
  // ⚠️ HISTORICAL. This was step 1's answer to `submit_finalize_blueprint` while
  // step 2 didn't exist, and the whole flow was shaped around treating it as a
  // success. §6a removed it: the lock now works and answers with a step change.
  // The constant is kept because an older backend still in flight would send it,
  // and silently mapping it to a generic failure would be worse than saying so.
  NOT_YET_AVAILABLE: "not_yet_available",
};

// Branch on `code`, never on `message` — same rule the rest of EXW follows.
export const LIVE_REVIEW_ERROR_COPY = {
  [LIVE_REVIEW_ERROR_CODE.NOT_AUTHORIZED]:
    "You don't have access to this live review session.",
  [LIVE_REVIEW_ERROR_CODE.LIVE_REVIEW_NOT_STARTED]:
    "This live review hasn't been started yet.",
  [LIVE_REVIEW_ERROR_CODE.ITEM_NOT_FOUND]:
    "That item is no longer on this blueprint. Refreshing…",
  [LIVE_REVIEW_ERROR_CODE.WRONG_STEP]:
    "That action doesn't belong to this step of the live review.",
  [LIVE_REVIEW_ERROR_CODE.REMARKS_REMAINING]:
    "There are still points left to discuss.",
  [LIVE_REVIEW_ERROR_CODE.REMARK_NOT_FOUND]:
    "That discussion point has already been resolved. Refreshing…",
  [LIVE_REVIEW_ERROR_CODE.WIGS_LOCKED]:
    "The blueprint is locked — item values can't be changed any more.",
  // Deliberately specific about what to do next, because §8 says not to retry
  // this one as-is: the connection the user drew isn't structurally possible.
  [LIVE_REVIEW_ERROR_CODE.INVALID_CONNECTOR]:
    "That connection isn't possible — a WIG can't connect to itself, and the BHAG can't connect to itself.",
  [LIVE_REVIEW_ERROR_CODE.DUPLICATE_CONNECTOR]:
    "This connection already exists.",
  [LIVE_REVIEW_ERROR_CODE.CONNECTOR_NOT_FOUND]:
    "That connection no longer exists. Refreshing…",
  [LIVE_REVIEW_ERROR_CODE.SECTION_NOT_FOUND]:
    "That section is no longer on this blueprint. Refreshing…",
  // Names the number rather than saying "the limit": the CEO is looking at the
  // section and can count it, so a limit they can't see is just a refusal.
  [LIVE_REVIEW_ERROR_CODE.ITEM_LIMIT_EXCEEDED]:
    "This dimension is full — 10 WIGs is the maximum. Remove one first.",
  [LIVE_REVIEW_ERROR_CODE.ITEM_FLOOR_REACHED]:
    "Every dimension needs at least one WIG, so this one can't be removed.",
  // The order we sent didn't match the section the server has — almost always
  // because the other person changed it a moment earlier. Says so, because
  // "invalid" would read as the user having done something wrong.
  [LIVE_REVIEW_ERROR_CODE.INVALID_REORDER]:
    "This dimension changed while you were moving things. Refreshing…",
  [LIVE_REVIEW_ERROR_CODE.INVALID_OWNER]:
    "That person isn't an active member of this company any more. Pick someone else.",
  [LIVE_REVIEW_ERROR_CODE.BLUEPRINT_ALREADY_LOCKED]:
    "This blueprint is locked — your prioritised WIGs can't be changed any more.",
  [LIVE_REVIEW_ERROR_CODE.NOT_YET_AVAILABLE]:
    "You're all set here — the next step isn't available yet.",
};

// §2 / §6a / §9. Three reachable steps now, not one. `live_review_step` is the
// screen router: §6a.4 is explicit that a re-`join` returns the current step and
// "that is what routes your screen, not which broadcast you happened to catch."
//
// ⚠️ There is deliberately NO step past `connectors_review`. §9: nothing moves
// `live_review_step` further and no "Confirm Connections" action exists — don't
// build that button.
export const LIVE_REVIEW_STEP = {
  FINALIZE_BLUEPRINT: "finalize_blueprint",
  CONNECTORS_DRAFTING: "connectors_drafting",
  CONNECTORS_REVIEW: "connectors_review",
  // §6b. The journey now genuinely ends: `year1_review` is the last step, and
  // what follows it is not another step but `blueprint_state: "LOCKED"`.
  YEAR1_DRAFTING: "year1_drafting",
  YEAR1_REVIEW: "year1_review",
};

// ⚠️ TWO LOCKS, AND SINCE HANDOFF #25 ONLY ONE OF THEM STOPS EDITING.
//
//   `isBlueprintLocked` — §6b's "Confirm Year 1 Plan". The TERMINAL lock. The
//                         whole blueprint is read-only; nothing is editable
//                         anywhere, and there is no step after it.
//   `isWigsLocked`      — §6a's "Lock Blueprint". It still happens, and it is
//                         still the moment the connectors agent starts, but as
//                         of #25 §2 it NO LONGER FREEZES ANYTHING. See the
//                         predicate itself for the full note.
//
// They read different fields — the step vs `blueprint_state`/`locked_at` —
// because the terminal lock is not a step. `wigs_locked_at` and `locked_at` are
// likewise two different timestamps; the handoff calls that out explicitly.
// ── How many WIGs a section may hold, during Live Review ────────────────────
// Server-enforced (handoff #25 §1): `add_live_review_item` answers
// `item_limit_exceeded` at the ceiling and `delete_live_review_item` answers
// `item_floor_reached` at the floor.
//
// ⚠️ DELIBERATELY NOT `MAX_SECTION_ITEMS` FROM `_config`. That constant is the
// CEO app's INTERVIEW bound, it is a product decision currently set to 7, and
// the coach app has no copy of that file at all — this module is byte-identical
// across both repos and cannot import it. Two different numbers for two
// different stages is the honest state of things, not drift: if they are ever
// meant to be one number, that is a product decision to make explicitly rather
// than by quietly importing one into the other.
export const LIVE_REVIEW_SECTION_MAX_ITEMS = 10;
export const LIVE_REVIEW_SECTION_MIN_ITEMS = 1;

export const BLUEPRINT_STATE_LOCKED = "LOCKED";

export const isBlueprintLocked = (snapshot) =>
  !!snapshot &&
  (snapshot.blueprint_state === BLUEPRINT_STATE_LOCKED ||
    snapshot.locked_at != null);

// §6a's graph vocabulary. An edge endpoint is a WIG item or the BHAG root, and
// `*_item_id` is null EXACTLY when the matching `*_node_type` is "bhag" — the
// handoff states that as a guarantee, so `isBhagEnd` below trusts the node type
// and never infers from a null id.
export const NODE_TYPE = {
  ITEM: "item",
  BHAG: "bhag",
};

// `ai_suggested` is the only value the handoff shows. A human-drawn connector's
// `source` is NOT documented — `manual` is this code's assumption, used only as
// the optimistic label on a connector we just created; the server's own value
// replaces it the moment `connector_added` echoes back. So a wrong guess here is
// visible for one round trip and never persisted.
export const CONNECTOR_SOURCE = {
  AI_SUGGESTED: "ai_suggested",
  MANUAL: "manual",
};

/**
 * Can the WIGs still be changed?
 *
 * ⚠️ THIS USED TO BE "is the step past `finalize_blueprint`" AND THAT IS NOW
 * WRONG (handoff #25 §2, 2026-09-14). It was right when written: the server
 * rejected every edit with `wigs_locked` the moment the CEO clicked "Lock
 * Blueprint", regardless of which stage Live Review had reached.
 *
 * #25 widened that deliberately. Add, edit, delete and reorder all work for as
 * long as `blueprint_state === 'LIVE_REVIEW_IN_PROGRESS'` — through connectors
 * and Year 1 as well as finalize — and stop only at the terminal lock. The
 * handoff is explicit: "the `wigs_locked` error code still exists but can now
 * only fire once the blueprint is actually `LOCKED`."
 *
 * So this is now the terminal lock, and the old step test would have left the
 * FE refusing edits the server was perfectly willing to accept in stages 2 and
 * 3 — the entire point of the #25 change, hidden behind a client-side guard.
 *
 * It stays a separate name rather than collapsing into `isBlueprintLocked`
 * because the two questions are still different ones — "may these values
 * change" vs "is the whole blueprint finished" — and the call sites read better
 * for it. They simply have the same answer now.
 */
export const isWigsLocked = (snapshot) => isBlueprintLocked(snapshot);

// `mark_remark_discussed` takes `{ remark_type, id }`.
//
// ⚠️ `id` is the REMARK's own id from the remarks list — NOT the item id. §6
// bolds this, and it is the kind of mistake that fails silently: passing an
// item id where a comment-remark id belongs will usually match some other
// remark's id and mark the wrong thing discussed. The emitter below takes the
// remark object itself for exactly this reason, so no call site handles the
// two ids side by side.
export const REMARK_TYPE = {
  COMMENT: "comment",
  ITEM: "item",
};

// The coach's per-item verdict. Same two values as the async review's
// `review_status`, deliberately — a live session continues the same decision,
// it doesn't start a parallel one.
export const LIVE_REVIEW_STATUS = {
  ACCEPTED: "accepted",
  TO_DISCUSS: "to_discuss",
};

export const PARTICIPANT_ROLE = {
  CEO: "ceo",
  COACH: "coach",
};

export const PARTICIPANT_ROLE_LABEL = {
  [PARTICIPANT_ROLE.CEO]: "CEO",
  [PARTICIPANT_ROLE.COACH]: "Coach",
};

// §5: a bad token gets the socket disconnected with NO preceding `error` event,
// so "disconnected almost immediately after connecting, without ever having
// joined" is the only available auth-failure signal. 2s is generous for a
// handshake rejection while staying well clear of a genuine mid-session drop.
const AUTH_FAILURE_WINDOW_MS = 2000;

// ─── Id coercion ─────────────────────────────────────────────────────────────
// Every item id is compared as a string, always.
//
// Ported from the CEO app's `executionBlueprintStore`, where it was added after
// real breakage: the backend sends an item id as a string in one event, a
// number in another, and bigint-serialised in a third. `===` then quietly fails
// and a delta lands on nothing — leaving a spinner spinning forever with no
// error anywhere. Cheap to apply on every boundary; expensive to debug once.
export const itemKey = (id) => (id == null ? "" : String(id));

// ─── Normalisers ─────────────────────────────────────────────────────────────
// The snapshot in §6 already lines up with what the coach's async-review screen
// renders (`section_key`, `execution_blueprint_item_id`, `coach_comment_id`,
// `review_status`, `reviewed_at`), which is why these are thin. They exist so
// that the four delta events — whose payload shapes are NOT in the handoff —
// have exactly one place to be reconciled, and so a field arriving nested
// doesn't silently read as `undefined`.
//
// `??` throughout, never `||`: an empty-string item value and a `0` count are
// both legitimate and must not fall through to a default.

const firstDefined = (...values) => {
  for (let i = 0; i < values.length; i += 1) {
    if (values[i] !== undefined && values[i] !== null) return values[i];
  }
  return null;
};

// A delta may arrive flat or wrapped — `{ item: {...} }`, `{ data: {...} }` —
// and we have no confirmed shape for any of the four. Unwrap one level rather
// than assume, so a wrapped payload doesn't read as entirely empty.
const unwrap = (raw, ...keys) => {
  if (!raw || typeof raw !== "object") return {};
  for (let i = 0; i < keys.length; i += 1) {
    const nested = raw[keys[i]];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      return nested;
    }
  }
  return raw;
};

const normalizeComment = (raw) => ({
  ...raw,
  // Canvas's own field name, so `CommentList` / `CommentItem` work unmodified —
  // the same fallback chain the coach's async-review normaliser uses.
  coach_comment_id: firstDefined(
    raw?.coach_comment_id,
    raw?.execution_blueprint_comment_id,
    raw?.comment_id,
    raw?.id,
  ),
});

// §6b: these four now ride on EVERY item in the snapshot, at every step, with
// `false`/`null` defaults before the shortlist agent has run. Defaulted here
// rather than at each call site so a screen can read `item.is_year1_shortlisted`
// without a guard, and so an older backend that omits them behaves as
// "not shortlisted, no owner" instead of rendering `undefined`.
// ── Owners: plural on the wire since handoff #25 §4 ────────────────────────
// `owner_user_ids: number[]` and `owners: [{ user_id, name }]` are the real
// fields now. `owner_user_id` / `owner_name` are still sent by the server, and
// still read here, but they mean "the FIRST owner" rather than "the owner".
//
// ⚠️ THE SINGULAR FIELDS ARE DERIVED, NOT TRUSTED. The server does send them,
// but deriving them from the plural pair keeps them true after an optimistic
// local write — where we set the array ourselves and never hear a singular
// value at all. Any screen not yet updated for multi-owner keeps reading them
// exactly as before and shows the first owner, which is the documented
// fallback rather than an accident.
const normalizeOwners = (raw) => {
  const owners = Array.isArray(raw?.owners) ? raw.owners : [];
  // Three sources, in order of authority. The middle one matters: a payload
  // carrying `owners` but not `owner_user_ids` would otherwise normalise to an
  // empty id list beside a non-empty owner list — two fields describing the
  // same thing and disagreeing, which is the exact failure this helper exists
  // to prevent.
  const ids = Array.isArray(raw?.owner_user_ids)
    ? raw.owner_user_ids
    : owners.length
      ? owners.map((owner) => owner?.user_id).filter((id) => id != null)
      : // Older payload (or an optimistic write that only set the singular
        // field): lift the one id into a one-element list so everything
        // downstream can read the array and only the array.
        [firstDefined(raw?.owner_user_id)].filter((id) => id != null);

  const first = owners[0] || null;
  return {
    owner_user_ids: ids,
    owners,
    // Kept in the shape every existing reader expects.
    owner_user_id: ids.length ? ids[0] : null,
    owner_name: first
      ? firstDefined(first.name)
      : firstDefined(raw?.owner_name),
  };
};

const normalizeYear1Fields = (raw) => ({
  is_year1_shortlisted: raw?.is_year1_shortlisted === true,
  ...normalizeOwners(raw),
  // A styling hint only — "the agent picked this and nobody has confirmed it".
  // Any deliberate pick clears it (§6b.6), so it must default to false, never
  // to true.
  owner_suggested_by_agent: raw?.owner_suggested_by_agent === true,
});

const normalizeItem = (raw) => ({
  ...raw,
  ...normalizeYear1Fields(raw),
  execution_blueprint_item_id: firstDefined(
    raw?.execution_blueprint_item_id,
    raw?.item_id,
    raw?.id,
  ),
  // The coach's verdict (`accepted` / `to_discuss`). Distinct from the raw
  // `status` field, which is the CEO's AUTHORING status (`EMPTY` … `FLAGGED`)
  // and must never be conflated with it — they answer different questions and
  // are set by different people.
  review_status: firstDefined(raw?.review_status, raw?.coach_review?.status),
  reviewed_at: firstDefined(raw?.reviewed_at, raw?.coach_review?.discussed_at),
  comments: (raw?.comments || []).map(normalizeComment),
});

const normalizeSection = (raw) => ({
  ...raw,
  items: (raw?.items || []).map(normalizeItem),
  // The async review calls these `section_comments`; the live snapshot may use
  // either. Accept both rather than render a section's notes as absent.
  comments: (raw?.section_comments || raw?.comments || []).map(
    normalizeComment,
  ),
});

// A remark is one outstanding thing to talk through — either a coach comment or
// a flagged item. `id` here is the remark's own id, which is what
// `mark_remark_discussed` wants; the item id travels alongside it and is only
// for locating the row on screen.
const normalizeRemark = (raw) => ({
  ...raw,
  id: firstDefined(raw?.id, raw?.remark_id),
  remark_type: raw?.remark_type ?? REMARK_TYPE.ITEM,
  discussed: raw?.discussed === true,
  discussed_at: firstDefined(raw?.discussed_at),
});

// §7: `participants` is ALWAYS a 2-item array — one CEO slot, one coach slot,
// both present even if that person has never joined. This rebuilds that
// guarantee rather than trusting it, so the presence indicator can render two
// rows unconditionally and never has to handle a missing slot.
//
// `user_id`/`name` stay `null` until a role has connected once, and then stay
// known after they leave — so the UI can say "Coach — Jane — offline" instead
// of "Unknown". Merging against the previous slot is what preserves that
// through a `presence_update` that omits the name.
const normalizeParticipants = (raw, previous) => {
  const incoming = Array.isArray(raw) ? raw : [];
  const previousByRole = {};
  (previous || []).forEach((slot) => {
    if (slot?.role) previousByRole[slot.role] = slot;
  });

  return [PARTICIPANT_ROLE.CEO, PARTICIPANT_ROLE.COACH].map((role) => {
    const next = incoming.find((slot) => slot?.role === role) || {};
    const prev = previousByRole[role] || {};
    return {
      role,
      // Never regress a known identity to null: once we know who the coach is,
      // that is true for the rest of the session.
      user_id: firstDefined(next.user_id, prev.user_id),
      name: firstDefined(next.name, prev.name),
      connected: next.connected === true,
      joined_at: firstDefined(next.joined_at, prev.joined_at),
      last_seen_at: firstDefined(next.last_seen_at, prev.last_seen_at),
    };
  });
};

// ─── The connectors graph (§6a.5) ────────────────────────────────────────────
// `{ nodes: { bhag, items[] }, edges[], overview }`.
//
// Two properties of this payload are load-bearing and easy to get wrong:
//
//  1. **An edge endpoint is the BHAG exactly when its `*_node_type` is "bhag",
//     and then its `*_item_id` is null.** So the node type is the discriminator
//     and the null id is a consequence — never test the id to decide which kind
//     of endpoint you have, because a missing item id on an `item` endpoint is a
//     malformed edge, not a BHAG.
//  2. **An agent that failed twice still sends this block, just empty** —
//     `edges: []` and every count `0`. That is an empty-graph state to render,
//     NOT an error, and the difference matters because the two look identical
//     from the data alone. `connectors` being *absent* is the only "we don't
//     have it yet" signal.
export const isBhagEnd = (nodeType) => nodeType === NODE_TYPE.BHAG;

const normalizeEdge = (raw) => {
  const fromType = raw?.from_node_type ?? NODE_TYPE.ITEM;
  const toType = raw?.to_node_type ?? NODE_TYPE.ITEM;
  return {
    ...raw,
    // The handoff calls this `id` on an edge but
    // `execution_blueprint_item_connector_id` in the three emit payloads. Both
    // are accepted here and normalised to `id`, so call sites have one name —
    // and `connectorIdOf` below is what turns it back into the wire name.
    id: firstDefined(raw?.id, raw?.execution_blueprint_item_connector_id),
    from_node_type: fromType,
    to_node_type: toType,
    // Forced to null on a BHAG end rather than passed through, so a server that
    // ever sends a stray id there can't make an item lookup succeed by accident.
    from_item_id: isBhagEnd(fromType) ? null : firstDefined(raw?.from_item_id),
    to_item_id: isBhagEnd(toType) ? null : firstDefined(raw?.to_item_id),
    source: firstDefined(raw?.source, CONNECTOR_SOURCE.AI_SUGGESTED),
  };
};

// The wire name for an edge id, for the three connector emits.
export const connectorIdOf = (edge) =>
  firstDefined(edge?.execution_blueprint_item_connector_id, edge?.id);

// `overview` counts are the SERVER's, always — every connector broadcast carries
// a freshly recomputed copy and §6a says to update the panel straight from it.
// Deriving them locally would be a second source of truth for numbers two people
// read off each other's screens, the same mistake `liveReviewAgenda.js` exists to
// avoid for the remarks counters.
//
// ⚠️ `unreviewed_count` is currently ALWAYS equal to `ai_suggested_count` —
// there is no per-connector review action behind it. The handoff flags this as a
// known limitation and says not to build UI logic expecting the two to diverge.
const normalizeOverview = (raw) => ({
  total_wigs: firstDefined(raw?.total_wigs) ?? 0,
  ai_suggested_count: firstDefined(raw?.ai_suggested_count) ?? 0,
  total_connections: firstDefined(raw?.total_connections) ?? 0,
  unreviewed_count: firstDefined(raw?.unreviewed_count) ?? 0,
});

export const normalizeConnectors = (raw) => {
  if (!raw) return null;
  const data = unwrap(raw, "connectors", "data");
  const nodes = data?.nodes || {};
  return {
    nodes: {
      // The BHAG arrives as an object with a label, not a bare string. Kept as
      // an object so a later id/status field doesn't change this shape.
      bhag: nodes.bhag
        ? { ...nodes.bhag, label: firstDefined(nodes.bhag.label) }
        : null,
      items: (nodes.items || []).map((item) => ({
        ...item,
        execution_blueprint_item_id: firstDefined(
          item?.execution_blueprint_item_id,
          item?.item_id,
          item?.id,
        ),
        section_key: firstDefined(item?.section_key),
        label: firstDefined(item?.label),
        type: firstDefined(item?.type),
      })),
    },
    edges: (data?.edges || []).map(normalizeEdge),
    overview: normalizeOverview(data?.overview),
  };
};

// ─── Year 1 (§6b.4) ──────────────────────────────────────────────────────────
// `{ items, roster }`. The items are the SAME items as `sections[].items[]`,
// carrying the same four Year 1 fields — the block is a convenience view, not a
// separate entity. That is why the reducers below patch the SECTIONS tree and
// not this block: one place to hold an item's truth, so a shortlist toggle can
// never leave the two disagreeing.
//
// `roster` is the owner picker's source. It only appears once the step reaches
// `year1_drafting`/`year1_review` or the blueprint is LOCKED, so an absent
// roster is normal earlier on and must not read as "nobody to assign".
const normalizeRosterMember = (raw) => ({
  ...raw,
  user_id: firstDefined(raw?.user_id, raw?.id),
  name: firstDefined(raw?.name),
  role_id: firstDefined(raw?.role_id),
});

export const normalizeYear1 = (raw) => {
  if (!raw) return null;
  const data = unwrap(raw, "year1", "data");
  return {
    items: (data?.items || []).map((item) => ({
      ...item,
      ...normalizeYear1Fields(item),
      execution_blueprint_item_id: firstDefined(
        item?.execution_blueprint_item_id,
        item?.item_id,
        item?.id,
      ),
      section_key: firstDefined(item?.section_key),
      label: firstDefined(item?.label),
    })),
    roster: (data?.roster || []).map(normalizeRosterMember),
  };
};

export const normalizeSnapshot = (raw, previous) => {
  if (!raw) return null;
  const data = unwrap(raw, "data");
  return {
    ...data,
    execution_blueprint_id: firstDefined(data.execution_blueprint_id),
    unique_id: firstDefined(data.unique_id),
    blueprint_state: firstDefined(data.blueprint_state),
    live_review_step: firstDefined(data.live_review_step),
    // ⚠️ TWO timestamps, and the handoff says in as many words not to confuse
    // them. `wigs_locked_at` is §6a's value-freeze; `locked_at` is §6b's
    // terminal lock. Both are carried; `isWigsLocked` / `isBlueprintLocked` are
    // the predicates that read them correctly.
    wigs_locked_at: firstDefined(data.wigs_locked_at),
    locked_at: firstDefined(data.locked_at),
    // Only present from `year1_drafting` onward. Falls back to what we already
    // held so a later snapshot that omits it can't empty the owner picker.
    roster: (data.roster || previous?.roster || []).map(normalizeRosterMember),
    // Absent (null) until the agent has produced it. Distinct from an EMPTY
    // graph, which is a real answer — see the note above `isBhagEnd`. Falls back
    // to whatever we already held so a snapshot that omits the block doesn't
    // wipe a graph we were already showing.
    connectors: data.connectors
      ? normalizeConnectors(data.connectors)
      : previous?.connectors ?? null,
    sections: (data.sections || []).map(normalizeSection),
    remarks: (data.remarks || []).map(normalizeRemark),
    // Counts come from the server and are never derived here. The async review
    // recomputes its own counts client-side because its mutation responses
    // return only the changed row; this screen gets a `remarks_updated` event
    // carrying fresh counts, so recomputing would be a second source of truth
    // for a number two people are reading off each other's screens.
    remarks_remaining: firstDefined(data.remarks_remaining) ?? 0,
    total_remarks: firstDefined(data.total_remarks) ?? 0,
    discussed_count: firstDefined(data.discussed_count) ?? 0,
    participants: normalizeParticipants(
      data.participants,
      previous?.participants,
    ),
  };
};

// ─── Pure reducers ───────────────────────────────────────────────────────────
// Split out from the store actions so they can be exercised directly by a node
// harness — this environment has no browser, and these four are where a wrong
// field name silently shows two people different data.

// Attribution: §6 says `item_updated` carries "who edited it" but does not name
// the field. Try the plausible role fields, then fall back to resolving a raw
// user_id against the participants list — which the snapshot always gives us,
// so this recovers a role even if the payload only identifies a person.
export const resolveActorRole = (payload, participants) => {
  const direct = firstDefined(
    payload?.updated_by_role,
    payload?.edited_by_role,
    payload?.actor_role,
    payload?.role,
  );
  if (direct === PARTICIPANT_ROLE.CEO || direct === PARTICIPANT_ROLE.COACH) {
    return direct;
  }

  const actorId = firstDefined(
    payload?.updated_by,
    payload?.edited_by,
    payload?.actor_id,
    payload?.user_id,
  );
  if (actorId == null) return null;

  const match = (participants || []).find(
    (slot) =>
      slot.user_id != null && itemKey(slot.user_id) === itemKey(actorId),
  );
  return match?.role ?? null;
};

// Walk every section's items and replace the one that matches, leaving the rest
// referentially identical so React re-renders one row rather than the tree.
// Read one item out of the sections tree. Exists for the optimistic owner
// write, which has to capture the CURRENT value before overwriting it so the
// rollback has something true to restore.
/** A comparable fingerprint of an owner list — order-insensitive. */
const ownerFingerprint = (ids) =>
  (ids || [])
    .map((id) => String(id))
    .sort()
    .join(",");

const findItem = (snapshot, targetId) => {
  const key = itemKey(targetId);
  if (!snapshot?.sections || !key) return null;
  for (const section of snapshot.sections) {
    for (const item of section.items || []) {
      if (itemKey(item.execution_blueprint_item_id) === key) return item;
    }
  }
  return null;
};

const patchItem = (snapshot, targetId, patch) => {
  const key = itemKey(targetId);
  if (!snapshot || !key) return snapshot;

  let found = false;
  const sections = snapshot.sections.map((section) => {
    let sectionChanged = false;
    const items = section.items.map((item) => {
      if (itemKey(item.execution_blueprint_item_id) !== key) return item;
      found = true;
      sectionChanged = true;
      return { ...item, ...patch };
    });
    return sectionChanged ? { ...section, items } : section;
  });

  // An unmatched id means our tree is stale — the caller turns this into a
  // resnapshot rather than pretending the delta was applied.
  if (!found) return null;
  return { ...snapshot, sections };
};

export const reduceItemUpdated = (snapshot, raw) => {
  const payload = unwrap(raw, "item", "data");
  const targetId = firstDefined(
    payload.execution_blueprint_item_id,
    payload.item_id,
    payload.id,
  );
  // `value` is read with `??` and NOT defaulted: clearing an item to an empty
  // string is a legitimate edit, and `|| ""` here would be indistinguishable
  // from a payload that omitted the field entirely.
  const nextValue = payload.value;
  // E27: the WIG's short name can now be edited too. Applied only when the
  // broadcast carries it — until the backend echoes `label`, a name edit simply
  // doesn't land, and the row keeps showing the name the server has.
  const nextLabel = payload.label;
  if (nextValue === undefined && nextLabel === undefined) return snapshot;

  return patchItem(snapshot, targetId, {
    ...(nextValue !== undefined ? { value: nextValue } : {}),
    ...(nextLabel !== undefined ? { label: nextLabel } : {}),
    _updatedByRole: resolveActorRole(payload, snapshot?.participants),
    _updatedAt:
      firstDefined(payload.updated_at, payload.edited_at) ??
      new Date().toISOString(),
  });
};

export const reduceItemStatusUpdated = (snapshot, raw) => {
  const payload = unwrap(raw, "item", "data");
  const targetId = firstDefined(
    payload.execution_blueprint_item_id,
    payload.item_id,
    payload.id,
  );
  const nextStatus = firstDefined(payload.review_status, payload.status);
  if (nextStatus == null) return snapshot;

  return patchItem(snapshot, targetId, {
    review_status: nextStatus,
    reviewed_at: firstDefined(payload.reviewed_at) ?? new Date().toISOString(),
    _updatedByRole: resolveActorRole(payload, snapshot?.participants),
  });
};

// ⚠️ The remarks list and its counters come from `remarks_updated` ONLY.
//
// §6 is explicit: this event fires after `mark_remark_discussed` AND always
// alongside `item_status_updated`, "because accepting an item can silently
// shrink the remarks list." So deriving the agenda from item statuses — which
// is what the coach's async-review screen does with `buildReviewSummary` — is
// wrong here: it would drift from the server's own `remarks_remaining` the
// first time an accept resolved a remark, and `remarks_remaining` is what gates
// the Continue action.
//
// Tolerates a counts-only payload: if `remarks` is absent the list is left
// alone and only the counters are patched, rather than blanking the agenda.
export const reduceRemarksUpdated = (snapshot, raw) => {
  if (!snapshot) return snapshot;
  const payload = unwrap(raw, "data");
  const next = { ...snapshot };

  if (Array.isArray(payload.remarks)) {
    next.remarks = payload.remarks.map(normalizeRemark);
  }
  const remaining = firstDefined(payload.remarks_remaining);
  const total = firstDefined(payload.total_remarks);
  const discussed = firstDefined(payload.discussed_count);
  if (remaining != null) next.remarks_remaining = remaining;
  if (total != null) next.total_remarks = total;
  if (discussed != null) next.discussed_count = discussed;

  return next;
};

// ─── §6a reducers ────────────────────────────────────────────────────────────

// `live_review_step_changed`. Fires twice after a lock: `connectors_drafting`
// with `wigs_locked_at`, then `connectors_review` with `connectors` attached.
// Both are handled by the same reducer because the only difference is which
// optional fields ride along, and treating them separately would mean two places
// to keep the step write correct.
export const reduceStepChanged = (snapshot, raw) => {
  if (!snapshot) return snapshot;
  const payload = unwrap(raw, "data");
  const nextStep = firstDefined(payload.live_review_step);
  if (nextStep == null) return snapshot;

  const next = { ...snapshot, live_review_step: nextStep };
  const lockedAt = firstDefined(payload.wigs_locked_at);
  if (lockedAt != null) next.wigs_locked_at = lockedAt;
  // Only overwrite the graph when this broadcast actually carries one. The
  // drafting broadcast doesn't, and clobbering with null would blank a graph on
  // the way *back* into review after a reconnect.
  if (payload.connectors)
    next.connectors = normalizeConnectors(payload.connectors);

  // §6b, same rule for the Year 1 block: `year1_drafting` carries nothing,
  // `year1_review` carries `{ items, roster }`.
  if (payload.year1) {
    const year1 = normalizeYear1(payload.year1);
    // The roster is lifted to the top level as well as kept on the block,
    // because that is where the snapshot puts it and the owner picker should
    // have one place to read it from regardless of which event delivered it.
    if (year1.roster.length) next.roster = year1.roster;
    // ⚠️ The items are folded INTO the sections tree rather than stored beside
    // it. `year1.items` and `sections[].items[]` are the same items with the
    // same four fields, so keeping both would create two truths for one
    // shortlist checkbox — and the delta events below patch the sections tree.
    next.sections = mergeYear1IntoSections(next.sections, year1.items);
  }
  return next;
};

// Applies the Year 1 block's four fields onto the matching items in the
// sections tree, leaving every unmatched section referentially identical so
// React re-renders only what changed.
const mergeYear1IntoSections = (sections, year1Items) => {
  if (!year1Items?.length) return sections;
  const byId = {};
  year1Items.forEach((item) => {
    byId[itemKey(item.execution_blueprint_item_id)] = item;
  });

  return (sections || []).map((section) => {
    let changed = false;
    const items = (section.items || []).map((item) => {
      const match = byId[itemKey(item.execution_blueprint_item_id)];
      if (!match) return item;
      changed = true;
      return { ...item, ...normalizeYear1Fields(match) };
    });
    return changed ? { ...section, items } : section;
  });
};

// `item_shortlist_updated`. One field, but it goes through `patchItem` like
// every other delta so an id we don't hold triggers a resnapshot rather than
// being dropped.
export const reduceShortlistUpdated = (snapshot, raw) => {
  const payload = unwrap(raw, "item", "data");
  const targetId = firstDefined(
    payload.execution_blueprint_item_id,
    payload.item_id,
    payload.id,
  );
  if (payload.is_year1_shortlisted === undefined) return snapshot;

  return patchItem(snapshot, targetId, {
    is_year1_shortlisted: payload.is_year1_shortlisted === true,
    year1_shortlisted_at: firstDefined(payload.year1_shortlisted_at),
  });
};

// `item_owner_updated`. ⚠️ `owner_suggested_by_agent` is read from the payload
// and NOT preserved from what we held: §6b.6 says this event always carries
// `false`, because picking anyone by hand counts as a human decision even when
// it happens to match the agent's suggestion. Defaulting to the old value here
// would leave the AI badge stuck on an owner a person just confirmed.
export const reduceOwnerUpdated = (snapshot, raw) => {
  const payload = unwrap(raw, "item", "data");
  const targetId = firstDefined(
    payload.execution_blueprint_item_id,
    payload.item_id,
    payload.id,
  );
  // An empty owner list is a legitimate value — it unassigns — so the presence
  // test has to be on the KEY, not on truthiness. Either spelling counts: #25
  // §4 keeps the singular field alongside the new plural one, and an older
  // server sends only the singular.
  if (!("owner_user_ids" in payload) && !("owner_user_id" in payload)) {
    return snapshot;
  }

  return patchItem(snapshot, targetId, {
    // Derives all four fields from whichever spelling the payload carried, so
    // the singular pair stays consistent with the list rather than being two
    // independently-read values that can disagree.
    ...normalizeOwners(payload),
    owner_assigned_at: firstDefined(payload.owner_assigned_at),
    owner_suggested_by_agent: payload.owner_suggested_by_agent === true,
  });
};

// `blueprint_locked` — the terminal lock landing. Broadcast once, no follow-up.
// Writes `blueprint_state` and `locked_at` rather than a step, because the
// terminal lock is NOT a step: `year1_review` remains the last one, and what
// changes is that everything on it becomes read-only.
export const reduceBlueprintLocked = (snapshot, raw) => {
  if (!snapshot) return snapshot;
  const payload = unwrap(raw, "data");
  return {
    ...snapshot,
    blueprint_state:
      firstDefined(payload.blueprint_state) ?? BLUEPRINT_STATE_LOCKED,
    locked_at: firstDefined(payload.locked_at) ?? new Date().toISOString(),
  };
};

// `connector_added` / `connector_updated`. One reducer, because an update is an
// in-place endpoint change on an existing id: patch if the id is present, append
// if it isn't. That also makes it idempotent, which matters because both parties
// receive the broadcast for their own action too.
export const reduceConnectorUpserted = (snapshot, raw) => {
  if (!snapshot?.connectors) return snapshot;
  const payload = unwrap(raw, "data");
  const incoming = payload.connector ? normalizeEdge(payload.connector) : null;
  if (!incoming || incoming.id == null) return snapshot;

  const edges = snapshot.connectors.edges || [];
  const at = edges.findIndex(
    (edge) => itemKey(edge.id) === itemKey(incoming.id),
  );
  const nextEdges =
    at === -1
      ? [...edges, incoming]
      : edges.map((edge, index) => (index === at ? incoming : edge));

  return {
    ...snapshot,
    connectors: {
      ...snapshot.connectors,
      edges: nextEdges,
      // Straight from the event, never recomputed — see `normalizeOverview`.
      overview: payload.overview
        ? normalizeOverview(payload.overview)
        : snapshot.connectors.overview,
    },
  };
};

export const reduceConnectorDeleted = (snapshot, raw) => {
  if (!snapshot?.connectors) return snapshot;
  const payload = unwrap(raw, "data");
  const goneId = firstDefined(
    payload.execution_blueprint_item_connector_id,
    payload.id,
  );
  if (goneId == null) return snapshot;

  return {
    ...snapshot,
    connectors: {
      ...snapshot.connectors,
      edges: (snapshot.connectors.edges || []).filter(
        (edge) => itemKey(edge.id) !== itemKey(goneId),
      ),
      overview: payload.overview
        ? normalizeOverview(payload.overview)
        : snapshot.connectors.overview,
    },
  };
};

/**
 * `section_items_reordered` — one section's items, in a new order.
 *
 * Reorders the EXISTING item objects rather than replacing them: the broadcast
 * carries only ids (`item_order`), so every other field — value, status,
 * owner, shortlist flag — lives on the objects we already hold. Rebuilding from
 * the payload would blank all of it.
 *
 * ⚠️ TOLERATES A PARTIAL ORDER, ON PURPOSE. The server sends every non-deleted
 * id, but an item added between our last snapshot and this broadcast would be
 * an id we don't know, and an item deleted in the same window would be one of
 * ours the order omits. Unknown ids are skipped and unmentioned items keep
 * their current relative position at the end, so the worst case is a row that
 * is briefly ordered slightly differently from the server's view and corrects
 * itself on the next snapshot — rather than items vanishing from the map.
 */
export const reduceSectionItemsReordered = (snapshot, raw) => {
  if (!snapshot?.sections) return snapshot;
  const payload = unwrap(raw, "data");
  const sectionKey = payload.section_key;
  const order = payload.item_order || payload.ordered_item_ids;
  if (!sectionKey || !Array.isArray(order)) return snapshot;

  const wanted = order.map((id) => itemKey(id));

  return {
    ...snapshot,
    sections: snapshot.sections.map((section) => {
      if (section.section_key !== sectionKey) return section;
      const items = section.items || [];

      const byId = new Map(
        items.map((item) => [itemKey(item.execution_blueprint_item_id), item]),
      );
      const ordered = wanted.map((id) => byId.get(id)).filter(Boolean);
      const rest = items.filter(
        (item) => !wanted.includes(itemKey(item.execution_blueprint_item_id)),
      );

      return { ...section, items: [...ordered, ...rest] };
    }),
  };
};

/**
 * `item_added` — a new WIG lands in a section.
 *
 * Appended rather than inserted at `item_order`: the server sends the order it
 * assigned, but a new item always goes to the end of its section, and trusting
 * the value would mean reconciling it against positions we hold that may
 * already differ by an in-flight reorder. Appending is what the server did.
 *
 * Idempotent. The broadcast reaches the person who added it too, and a
 * resnapshot can race it, so an id we already hold is left alone rather than
 * duplicated — a duplicate here would render two identical cards that cannot be
 * told apart.
 */
export const reduceItemAdded = (snapshot, raw) => {
  if (!snapshot?.sections) return snapshot;
  const payload = unwrap(raw, "item", "data");
  const sectionKey = payload.section_key;
  const id = firstDefined(payload.execution_blueprint_item_id, payload.id);
  if (!sectionKey || id == null) return snapshot;

  if (findItem(snapshot, id)) return snapshot;

  return {
    ...snapshot,
    sections: snapshot.sections.map((section) =>
      section.section_key === sectionKey
        ? {
            ...section,
            items: [...(section.items || []), normalizeItem(payload)],
          }
        : section,
    ),
  };
};

/**
 * `item_deleted` — a WIG goes, and its connectors go with it.
 *
 * Two removals in one reducer on purpose. The server cascades them in a single
 * transaction and reports them in a single event, so splitting them across two
 * state writes would put a frame on screen where the card is gone and its lines
 * are still drawn, hanging off nothing.
 *
 * The connector ids are the server's own, taken from `deleted_connector_ids`
 * rather than recomputed by matching edges against the item. Recomputing would
 * be a second implementation of the cascade rule — and the one place it would
 * differ is exactly where it matters, on a connector shape this build hasn't
 * seen yet.
 */
export const reduceItemDeleted = (snapshot, raw) => {
  if (!snapshot?.sections) return snapshot;
  const payload = unwrap(raw, "data");
  const id = firstDefined(payload.execution_blueprint_item_id, payload.item_id);
  if (id == null) return snapshot;

  const goneItem = itemKey(id);
  const goneEdges = new Set(
    (payload.deleted_connector_ids || []).map((edgeId) => itemKey(edgeId)),
  );

  const next = {
    ...snapshot,
    sections: snapshot.sections.map((section) => ({
      ...section,
      items: (section.items || []).filter(
        (item) => itemKey(item.execution_blueprint_item_id) !== goneItem,
      ),
    })),
  };

  if (!snapshot.connectors) return next;

  return {
    ...next,
    connectors: {
      ...snapshot.connectors,
      // Belt and braces: the id list is the contract, but an edge still
      // pointing at the deleted item is dropped too. A line to a card that no
      // longer exists has no geometry to draw against, so it would throw or
      // vanish silently depending on where it is read — neither is a state to
      // leave reachable on a payload we can't test against yet.
      edges: (snapshot.connectors.edges || []).filter(
        (edge) =>
          !goneEdges.has(itemKey(edge.id)) &&
          itemKey(edge.from) !== goneItem &&
          itemKey(edge.to) !== goneItem,
      ),
      // Not recomputed locally — §6a is explicit that the counts are the
      // server's. The next connector broadcast or snapshot carries fresh ones;
      // until then a total that is briefly one too high is better than a
      // second source of truth for a number two people read aloud.
      overview: payload.overview
        ? normalizeOverview(payload.overview)
        : snapshot.connectors.overview,
    },
  };
};

export const reducePresenceUpdate = (snapshot, raw) => {
  if (!snapshot) return snapshot;
  const payload = unwrap(raw, "data");
  // Either the whole 2-slot array, or a single-role delta. Both are plausible
  // and neither is documented, so a delta is lifted into an array of one and
  // merged against what we already know.
  const incoming = Array.isArray(payload.participants)
    ? payload.participants
    : Array.isArray(payload)
      ? payload
      : payload.role
        ? [payload]
        : [];
  return {
    ...snapshot,
    participants: normalizeParticipants(incoming, snapshot.participants),
  };
};

// ─── Store ───────────────────────────────────────────────────────────────────

const INITIAL_STATE = {
  socket: null,
  isConnected: false,
  listenersBound: false,

  uniqueId: null,
  // Which side of the conversation this app is. The one deliberately
  // app-specific value, passed in by the screen rather than branched on here —
  // see the file header.
  viewerRole: null,

  joining: false,
  joined: false,
  joinError: null,

  snapshot: null,
  // { code, message, at } — surfaced by the screen. This store deliberately
  // does NOT call a notification helper: the two repos have different ones, and
  // keeping this file identical matters more than saving the screens a line.
  lastError: null,
  // Set when the Continue action returns `not_yet_available` (§7), which is a
  // success as far as the user is concerned. Held separately from `lastError`
  // so it renders as reassurance rather than as a failure.
  continueState: null,
  // { [itemKey]: true } while an edit or status change is in flight. Keyed per
  // item so one row's spinner can't disable the others.
  pendingItems: {},
  // Previous owner per item, held only while an optimistic owner write is
  // waiting on its echo. See `setItemOwner` — this is what makes going
  // optimistic there reversible instead of a guess we can't take back.
  ownerRollback: {},
  // ⚠️ THE LAST OWNER LIST WE SENT, PER ITEM — the fix for the owner picker
  // flickering under rapid clicks.
  //
  // Every pick emits and echoes back. Click three people quickly and three
  // emits are in flight at once, so echo #1 — carrying ONE owner — lands after
  // the local state already shows three and resets the cell to one. The next
  // echo puts two back, the next three: the avatars visibly count up and down.
  //
  // So the local value is authoritative while a write is outstanding, and an
  // echo is only applied once it MATCHES what we last sent. Anything else is a
  // reply to a superseded click and is dropped.
  //
  // Keyed as a sorted, comma-joined string of ids, because that is the only
  // thing being compared — order on the wire is ours and the server's answer
  // need not preserve it.
  ownerInFlight: {},
  // `{ sectionKey, order }` — the item order to restore if an optimistic
  // reorder is rejected. One at a time: a second reorder can't be started until
  // the first drag has ended, and by then the echo has almost always landed.
  reorderRollback: null,
  // The `section_key` an add is in flight for, or null. A key rather than a
  // boolean so two sections' composers can be open at once and only the one
  // that was submitted shows as working.
  addPending: null,
  // Bumped whenever a delta arrives for an item we don't have. The screen
  // watches it and refetches, per §8's "Refresh the snapshot" guidance.
  resnapshotRequests: 0,
  // §6a. A single flag rather than a per-connector map, unlike `pendingItems`:
  // a connector mutation is a whole-canvas operation (the overview recomputes
  // with every one), and the canvas shouldn't accept a second drag until the
  // first has echoed back — an add and a delete crossing on the wire would
  // leave the two screens briefly disagreeing about what exists.
  connectorPending: false,
  // True between emitting the lock and the step-change broadcast that answers
  // it. Only used to disable the button so it can't be pressed twice — the
  // screen still moves on the broadcast, not on this.
  locking: false,
  // True between emitting `confirm_connections` and the step-change broadcast
  // that answers it. Only disables the button so it can't be pressed twice; the
  // screen still moves on the broadcast.
  advancing: false,
};

// Year 1 edits are valid only during `year1_review`, and never once the terminal
// lock has fired. Both halves matter: the step alone would still allow edits
// after `blueprint_locked`, because that lock is not a step change.
function _canEditYear1(snapshot) {
  return (
    snapshot?.live_review_step === LIVE_REVIEW_STEP.YEAR1_REVIEW &&
    !isBlueprintLocked(snapshot)
  );
}

// Builds one end of a connector payload. `itemId` is OMITTED for a BHAG end
// rather than sent as null, because §6a writes those fields as optional
// (`from_item_id?`) and an explicit null is a different thing from an absent key
// on a validating backend. Returns `{ from_node_type, from_item_id? }` etc.
function _endpoint(side, end) {
  const nodeType = end?.nodeType || NODE_TYPE.ITEM;
  const payload = { [`${side}_node_type`]: nodeType };
  if (!isBhagEnd(nodeType)) payload[`${side}_item_id`] = end?.itemId;
  return payload;
}

// Module scope, not store state: neither is renderable, and putting a DOM
// handler in a zustand store invites it into a selector.
let visibilityHandler = null;
let connectedAt = 0;

const liveReviewSocketStore = createStore((set, get) => ({
  ...INITIAL_STATE,

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  // Called once by the screen on mount. Idempotent: the `listenersBound` guard
  // plus the explicit `off` sweep below make a second call harmless, which is
  // what makes StrictMode's double mount safe without any StrictMode-specific
  // code.
  connect: ({ uniqueId, viewerRole }) => {
    const socket = unifiedProjectSocketManager.connect(LIVE_REVIEW_NAMESPACE);
    if (!socket) {
      set({
        lastError: {
          code: "namespace_missing",
          message:
            "Live review socket namespace is not registered in socket.js.",
          at: Date.now(),
        },
      });
      return;
    }

    const alreadyBound = get().listenersBound;
    set({ socket, uniqueId, viewerRole });

    if (alreadyBound) {
      // Listeners are live from a previous mount; just (re-)join, since the
      // uniqueId may have changed.
      get().join();
      return;
    }

    // Strip stale listeners before binding fresh ones. The manager keeps the
    // socket instance alive across mounts, so without this a remount stacks a
    // second copy of every handler and each event applies twice.
    //
    // Explicit lines rather than a loop over LIVE_REVIEW_ON, deliberately: a
    // loop would silently cover a future event and hide the fact that adding
    // one has a second obligation. Every entry below must have a matching `on`.
    socket.off("connect");
    socket.off("disconnect");
    socket.off("connect_error");
    socket.off(LIVE_REVIEW_ON.DETAILS);
    socket.off(LIVE_REVIEW_ON.ITEM_UPDATED);
    socket.off(LIVE_REVIEW_ON.ITEM_STATUS_UPDATED);
    socket.off(LIVE_REVIEW_ON.SECTION_ITEMS_REORDERED);
    socket.off(LIVE_REVIEW_ON.ITEM_ADDED);
    socket.off(LIVE_REVIEW_ON.ITEM_DELETED);
    socket.off(LIVE_REVIEW_ON.REMARKS_UPDATED);
    socket.off(LIVE_REVIEW_ON.PRESENCE_UPDATE);
    socket.off(LIVE_REVIEW_ON.STEP_CHANGED);
    socket.off(LIVE_REVIEW_ON.CONNECTOR_ADDED);
    socket.off(LIVE_REVIEW_ON.CONNECTOR_UPDATED);
    socket.off(LIVE_REVIEW_ON.CONNECTOR_DELETED);
    socket.off(LIVE_REVIEW_ON.ITEM_SHORTLIST_UPDATED);
    socket.off(LIVE_REVIEW_ON.ITEM_OWNER_UPDATED);
    socket.off(LIVE_REVIEW_ON.BLUEPRINT_LOCKED);
    socket.off(LIVE_REVIEW_ON.ITEM_SHORTLIST_UPDATED);
    socket.off(LIVE_REVIEW_ON.ITEM_OWNER_UPDATED);
    socket.off(LIVE_REVIEW_ON.BLUEPRINT_LOCKED);
    socket.off(LIVE_REVIEW_ON.ERROR);

    set({ listenersBound: true });

    socket.on("connect", () => {
      connectedAt = Date.now();
      set({ isConnected: true, joinError: null });
      // Re-join on every connect, not just the first. A reconnect drops us out
      // of the room server-side, and §5 says nothing works until `join`
      // succeeds — so without this a recovered socket looks connected and
      // silently receives nothing.
      get().join();
    });

    socket.on("disconnect", (reason) => {
      const wasJoined = get().joined;
      const elapsed = connectedAt ? Date.now() - connectedAt : Infinity;
      set({ isConnected: false, joined: false });

      // §5: a rejected handshake is disconnected with no `error` event first,
      // so this is the only place an auth failure can be detected.
      if (!wasJoined && elapsed < AUTH_FAILURE_WINDOW_MS) {
        // eslint-disable-next-line no-console
        console.error(
          "[LiveReviewSocket] Disconnected immediately after connecting " +
            "without joining — per handoff §5 this is how a rejected token " +
            "presents. The handshake sends the BARE access token; the handoff " +
            "asks for `Bearer <jwt>`. If this persists, flip the auth form in " +
            "src/socket.js (resolveSocketAuthToken / the liveReviewSocket " +
            "extraHeaders) and try again.",
          { reason },
        );
        set({
          joinError: {
            code: LIVE_REVIEW_ERROR_CODE.NOT_AUTHORIZED,
            message:
              LIVE_REVIEW_ERROR_COPY[LIVE_REVIEW_ERROR_CODE.NOT_AUTHORIZED],
            at: Date.now(),
          },
        });
      }
    });

    socket.on("connect_error", (err) => {
      set({
        joining: false,
        joinError: {
          code: "connect_error",
          message: err?.message || "Couldn't connect to the live review.",
          at: Date.now(),
        },
      });
    });

    // The full initial state, sent only to whoever just joined.
    socket.on(LIVE_REVIEW_ON.DETAILS, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.DETAILS);
      if (parsed === undefined) return;
      set((state) => ({
        snapshot: normalizeSnapshot(parsed, state.snapshot),
        joining: false,
        joined: true,
        joinError: null,
      }));
    });

    socket.on(LIVE_REVIEW_ON.ITEM_UPDATED, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.ITEM_UPDATED);
      if (parsed === undefined) return;
      applyReduced(set, get, reduceItemUpdated(get().snapshot, parsed), parsed);
    });

    socket.on(LIVE_REVIEW_ON.ITEM_ADDED, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.ITEM_ADDED);
      if (parsed === undefined) return;
      // Cleared whoever added it: the composer that is waiting belongs to this
      // section, and a second person adding to the same one at the same moment
      // releasing it early is harmless — the form is empty by then anyway.
      set({ addPending: null });
      applyReduced(set, get, reduceItemAdded(get().snapshot, parsed), parsed);
    });

    socket.on(LIVE_REVIEW_ON.ITEM_DELETED, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.ITEM_DELETED);
      if (parsed === undefined) return;
      const goneId = firstDefined(
        parsed?.execution_blueprint_item_id,
        parsed?.data?.execution_blueprint_item_id,
      );
      set((state) => {
        if (goneId == null) return {};
        // The row is gone, so its pending flag has nothing left to sit on.
        const pendingItems = { ...state.pendingItems };
        delete pendingItems[itemKey(goneId)];
        return { pendingItems };
      });
      applyReduced(set, get, reduceItemDeleted(get().snapshot, parsed), parsed);
    });

    socket.on(LIVE_REVIEW_ON.SECTION_ITEMS_REORDERED, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.SECTION_ITEMS_REORDERED);
      if (parsed === undefined) return;
      // The server's order supersedes whatever we guessed, so the rollback is
      // spent — including when this echo is somebody ELSE's reorder, since our
      // own optimistic write is no longer the newest thing on screen either way.
      set({ reorderRollback: null });
      applyReduced(
        set,
        get,
        reduceSectionItemsReordered(get().snapshot, parsed),
        parsed,
      );
    });

    socket.on(LIVE_REVIEW_ON.ITEM_STATUS_UPDATED, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.ITEM_STATUS_UPDATED);
      if (parsed === undefined) return;
      applyReduced(
        set,
        get,
        reduceItemStatusUpdated(get().snapshot, parsed),
        parsed,
      );
    });

    socket.on(LIVE_REVIEW_ON.REMARKS_UPDATED, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.REMARKS_UPDATED);
      if (parsed === undefined) return;
      set((state) => ({
        snapshot: reduceRemarksUpdated(state.snapshot, parsed),
      }));
    });

    socket.on(LIVE_REVIEW_ON.PRESENCE_UPDATE, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.PRESENCE_UPDATE);
      if (parsed === undefined) return;
      set((state) => ({
        snapshot: reducePresenceUpdate(state.snapshot, parsed),
      }));
    });

    // ── §6a ───────────────────────────────────────────────────────────────
    // The step change is what moves BOTH screens past the lock, so it also
    // clears any in-flight row spinners: every pending edit is moot the moment
    // values are frozen, and a spinner left on a row nobody can edit any more
    // would never resolve.
    socket.on(LIVE_REVIEW_ON.STEP_CHANGED, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.STEP_CHANGED);
      if (parsed === undefined) return;
      set((state) => ({
        snapshot: reduceStepChanged(state.snapshot, parsed),
        pendingItems: {},
        // A step change is the successful outcome of the lock, so clear any
        // error still on screen from before it.
        lastError: null,
      }));
    });

    socket.on(LIVE_REVIEW_ON.CONNECTOR_ADDED, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.CONNECTOR_ADDED);
      if (parsed === undefined) return;
      set((state) => ({
        snapshot: reduceConnectorUpserted(state.snapshot, parsed),
        connectorPending: false,
      }));
    });

    socket.on(LIVE_REVIEW_ON.CONNECTOR_UPDATED, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.CONNECTOR_UPDATED);
      if (parsed === undefined) return;
      set((state) => ({
        snapshot: reduceConnectorUpserted(state.snapshot, parsed),
        connectorPending: false,
      }));
    });

    socket.on(LIVE_REVIEW_ON.CONNECTOR_DELETED, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.CONNECTOR_DELETED);
      if (parsed === undefined) return;
      set((state) => ({
        snapshot: reduceConnectorDeleted(state.snapshot, parsed),
        connectorPending: false,
      }));
    });

    // ── §6b ───────────────────────────────────────────────────────────────
    socket.on(LIVE_REVIEW_ON.ITEM_SHORTLIST_UPDATED, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.ITEM_SHORTLIST_UPDATED);
      if (parsed === undefined) return;
      applyReduced(
        set,
        get,
        reduceShortlistUpdated(get().snapshot, parsed),
        parsed,
      );
    });

    socket.on(LIVE_REVIEW_ON.ITEM_OWNER_UPDATED, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.ITEM_OWNER_UPDATED);
      if (parsed === undefined) return;

      const body = unwrap(parsed, "item", "data");
      const settled = itemKey(
        firstDefined(body.execution_blueprint_item_id, body.item_id),
      );

      // ── Is this echo still current? ────────────────────────────────────────
      // While a write is outstanding the LOCAL value wins. An echo is applied
      // only when it matches what we last sent; a reply to a click the user has
      // already superseded is dropped, which is what stops the avatars counting
      // up and down under rapid picking.
      //
      // `owner_user_ids` absent means an older server that only speaks the
      // singular field — `normalizeOwners` lifts it, and the fingerprint of a
      // one-element list compares just the same.
      const inFlight = settled ? get().ownerInFlight[settled] : undefined;
      if (inFlight !== undefined) {
        const echoed = ownerFingerprint(normalizeOwners(body).owner_user_ids);
        if (echoed !== inFlight) return;
      }

      applyReduced(
        set,
        get,
        reduceOwnerUpdated(get().snapshot, parsed),
        parsed,
      );

      // The optimistic value has been confirmed by the server, so neither the
      // rollback nor the in-flight fingerprint is needed for this item any
      // more. Both dropped by id rather than cleared wholesale: another item's
      // write may still be outstanding and keeps its own.
      if (settled) {
        set((state) => {
          const next = {};
          if (state.ownerRollback[settled]) {
            const rollback = { ...state.ownerRollback };
            delete rollback[settled];
            next.ownerRollback = rollback;
          }
          if (state.ownerInFlight[settled] !== undefined) {
            const flight = { ...state.ownerInFlight };
            delete flight[settled];
            next.ownerInFlight = flight;
          }
          return next;
        });
      }
    });

    // The terminal lock. Clears every in-flight flag: nothing that was pending
    // can still land, and the screen is read-only from here.
    socket.on(LIVE_REVIEW_ON.BLUEPRINT_LOCKED, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.BLUEPRINT_LOCKED);
      if (parsed === undefined) return;
      set((state) => ({
        snapshot: reduceBlueprintLocked(state.snapshot, parsed),
        pendingItems: {},
        connectorPending: false,
        locking: false,
        lastError: null,
      }));
    });

    socket.on(LIVE_REVIEW_ON.ERROR, (raw) => {
      const parsed = parsePayload(raw, LIVE_REVIEW_ON.ERROR) || {};
      get()._handleServerError(parsed);
    });

    // Scoped tab-visibility recovery. The global handler in `socket.js`
    // deliberately skips this namespace — it can't tell a socket we still want
    // from one we closed on purpose, and reviving a closed one would report the
    // user as present on a screen they've left. Registering it here means it
    // exists exactly while the screen is mounted.
    if (typeof document !== "undefined" && !visibilityHandler) {
      visibilityHandler = () => {
        if (document.visibilityState !== "visible") return;
        const live = unifiedProjectSocketManager.getSocket(
          LIVE_REVIEW_NAMESPACE,
        );
        try {
          if (live && !live.connected) live.connect();
        } catch (e) {
          // Swallowed: nothing actionable, and the next tick retries.
        }
      };
      document.addEventListener("visibilitychange", visibilityHandler);
    }

    // The socket may already have been connected by the manager before the
    // "connect" handler above was bound, in which case that handler never
    // fires and the join never happens.
    if (socket.connected) {
      connectedAt = Date.now();
      set({ isConnected: true });
      get().join();
    }
  },

  disconnect: () => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (socket) {
      socket.off("connect");
      socket.off("disconnect");
      socket.off("connect_error");
      socket.off(LIVE_REVIEW_ON.DETAILS);
      socket.off(LIVE_REVIEW_ON.ITEM_UPDATED);
      socket.off(LIVE_REVIEW_ON.ITEM_STATUS_UPDATED);
      socket.off(LIVE_REVIEW_ON.SECTION_ITEMS_REORDERED);
      socket.off(LIVE_REVIEW_ON.ITEM_ADDED);
      socket.off(LIVE_REVIEW_ON.ITEM_DELETED);
      socket.off(LIVE_REVIEW_ON.REMARKS_UPDATED);
      socket.off(LIVE_REVIEW_ON.PRESENCE_UPDATE);
      socket.off(LIVE_REVIEW_ON.STEP_CHANGED);
      socket.off(LIVE_REVIEW_ON.CONNECTOR_ADDED);
      socket.off(LIVE_REVIEW_ON.CONNECTOR_UPDATED);
      socket.off(LIVE_REVIEW_ON.CONNECTOR_DELETED);
      socket.off(LIVE_REVIEW_ON.ERROR);
    }

    if (visibilityHandler && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", visibilityHandler);
      visibilityHandler = null;
    }
    connectedAt = 0;

    // `hardDisconnect`, not `disconnect` — the manager's `disconnect()` only
    // decrements a counter and leaves the transport open, which for this screen
    // would keep reporting the user as present in a session they have left.
    unifiedProjectSocketManager.hardDisconnect(LIVE_REVIEW_NAMESPACE);

    // Full reset. Unlike the chat store, which caches per-chat state across
    // disconnects so tab switches stay instant, a live session's state is
    // worthless once it ends — and stale sections rendered under a fresh join
    // would be actively misleading.
    set({ ...INITIAL_STATE });
  },

  // ── Emitters ──────────────────────────────────────────────────────────────
  // All follow the house shape: resolve the socket, bail if absent, emit. They
  // use `getSocket` rather than `connect` because a live socket is a
  // precondition here, not something an action should establish.

  join: () => {
    const { uniqueId } = get();
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket || !uniqueId) return;

    set({ joining: true });
    socket.emit(LIVE_REVIEW_EMIT.JOIN, { unique_id: uniqueId }, (ack) => {
      // §5's documented ack shape. A rejection here is paired with an `error`
      // event carrying the code, so this only has to clear the loading flag —
      // the specific message arrives separately.
      if (!ack?.acknowledgement) {
        set({ joining: false });
        return;
      }
      set({ joining: false, joined: true });
    });
  },

  // `label` is optional (E27). Sent only when the name actually changed, so a
  // wording-only edit is byte-for-byte the payload it always was, and a backend
  // that doesn't read `label` yet simply ignores it.
  editItem: (itemId, value, label) => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket) return;
    const key = itemKey(itemId);
    if (!key) return;
    // §6a: the server answers `wigs_locked` past the lock. Guarding here as well
    // as hiding the affordance is belt-and-braces on purpose — the UI decides
    // what to *offer* from the same step, but a stale click that slips through
    // (a step change landing between render and click) should die locally rather
    // than come back as an error banner the user can do nothing about.
    if (isWigsLocked(get().snapshot)) return;
    socket.emit(LIVE_REVIEW_EMIT.EDIT_ITEM, {
      execution_blueprint_item_id: itemId,
      value,
      ...(label !== undefined ? { label } : {}),
    });
    // No optimistic write. §6 broadcasts every successful action back to the
    // actor too, so `item_updated` is what applies this — and it is also what
    // the other party gets, which keeps the two screens rendering from exactly
    // one code path. The async review DOES apply optimistically because its
    // REST call has no echo; here an optimistic write would be a second,
    // divergent source for a value two people are watching at once.
  },

  setItemStatus: (itemId, status) => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket) return;
    const key = itemKey(itemId);
    if (!key) return;
    if (isWigsLocked(get().snapshot)) return;

    get()._setItemPending(key, true);
    socket.emit(LIVE_REVIEW_EMIT.SET_ITEM_STATUS, {
      execution_blueprint_item_id: itemId,
      status,
    });
  },

  // Takes the remark OBJECT, not an id, so that no call site ever has a remark
  // id and an item id in scope together — see REMARK_TYPE above for why that
  // particular mix-up is worth designing against.
  markRemarkDiscussed: (remark) => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket || !remark || remark.id == null) return;
    if (remark.discussed) return; // One-way; nothing to undo.

    socket.emit(LIVE_REVIEW_EMIT.MARK_REMARK_DISCUSSED, {
      remark_type: remark.remark_type,
      id: remark.id,
    });
  },

  // ⚠️ "Lock Blueprint" — PERMANENT as of §6a. It freezes every item value and
  // starts the connectors agent. There is no undo and no unlock endpoint, so the
  // caller must have confirmed with the user first.
  //
  // No payload (§6). The success path is NOT a reply to this emit — it is the
  // `live_review_step_changed` broadcast, which both parties get. So this
  // deliberately does not set any local "done" state: doing so would move the
  // clicker's screen ahead of their counterpart's.
  submitFinalize: () => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket) return;
    if (isWigsLocked(get().snapshot)) return;
    set({ continueState: null, lastError: null, locking: true });
    socket.emit(LIVE_REVIEW_EMIT.SUBMIT_FINALIZE);
  },

  // ── Connector editing (§6a) ───────────────────────────────────────────────
  // All three refuse outside `connectors_review`, matching the server's own
  // `wrong_step` guard, and all three set `connectorPending` so the canvas can
  // refuse a second drag until the first echoes back.
  //
  // `from`/`to` are `{ nodeType, itemId }`. `itemId` is dropped entirely for a
  // BHAG end rather than sent as null — §6a writes those payload fields as
  // optional (`from_item_id?`), so omitting is the documented shape.
  addConnector: (from, to) => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket) return;
    if (
      get().snapshot?.live_review_step !== LIVE_REVIEW_STEP.CONNECTORS_REVIEW
    ) {
      return;
    }
    set({ connectorPending: true, lastError: null });
    socket.emit(LIVE_REVIEW_EMIT.ADD_CONNECTOR, {
      ..._endpoint("from", from),
      ..._endpoint("to", to),
    });
  },

  updateConnector: (connectorId, from, to) => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket || connectorId == null) return;
    if (
      get().snapshot?.live_review_step !== LIVE_REVIEW_STEP.CONNECTORS_REVIEW
    ) {
      return;
    }
    set({ connectorPending: true, lastError: null });
    socket.emit(LIVE_REVIEW_EMIT.UPDATE_CONNECTOR, {
      execution_blueprint_item_connector_id: connectorId,
      ..._endpoint("from", from),
      ..._endpoint("to", to),
    });
  },

  /**
   * `add_live_review_item` — a new WIG in a section.
   *
   * @param sectionKey  The section's `section_key`.
   * @param draft       `{ label, value, rationale? }`.
   *
   * No optimistic write, deliberately — and this is the one action where that
   * is clearly right rather than a trade-off. The server assigns the id, and
   * everything downstream is keyed by it: the reorder wire, the connector
   * endpoints, the delete call. A card rendered under a temporary local id is a
   * card that cannot be connected, reordered or removed until the echo lands,
   * and is indistinguishable from a real one while it can't.
   *
   * `addPending` covers the gap instead, so the composer can show it is working
   * and refuse a second submission.
   */
  addLiveReviewItem: (sectionKey, draft) => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket || !sectionKey || !draft) return;
    if (isWigsLocked(get().snapshot)) return;

    const value = (draft.value || "").trim();
    if (!value) return;

    set({ addPending: sectionKey, lastError: null });
    socket.emit(LIVE_REVIEW_EMIT.ADD_ITEM, {
      section_key: sectionKey,
      // The server takes both; the label is the card's caption and the value is
      // the goal itself. An empty label is allowed — the card renders without a
      // caption, exactly as the interview's own user-added items do.
      label: (draft.label || "").trim(),
      value,
      rationale: (draft.rationale || "").trim() || null,
    });
  },

  /**
   * `delete_live_review_item` — remove a WIG, and with it every connector
   * touching either of its ends.
   *
   * No optimistic write either, and for a different reason: the cascade is the
   * server's to compute. Removing the card locally and guessing at its edges
   * would mean implementing the cascade rule twice, and the guess is visible —
   * lines disappearing that shouldn't, or surviving that should not.
   * `deleted_connector_ids` on the echo is the authoritative list.
   */
  deleteLiveReviewItem: (itemId) => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket || itemId == null) return;
    if (isWigsLocked(get().snapshot)) return;

    set((state) => ({
      lastError: null,
      pendingItems: { ...state.pendingItems, [itemKey(itemId)]: true },
    }));
    socket.emit(LIVE_REVIEW_EMIT.DELETE_ITEM, {
      execution_blueprint_item_id: itemId,
    });
  },

  /**
   * `reorder_section_items` — a section's items, in a new order.
   *
   * @param sectionKey  The section's `section_key`. NOT a band key: the BHAG
   *                    band has no section behind it and would be rejected as
   *                    `section_not_found`.
   * @param orderedIds  Every current, non-deleted item in that section, exactly
   *                    once, in the new order.
   *
   * ⚠️ NOT GATED ON A STEP, unlike the connector emits. Handoff #25 §2 widened
   * reordering to the whole of Live Review — it now fails only once the
   * blueprint is terminally locked, which the server answers with `wigs_locked`.
   *
   * ⚠️ OPTIMISTIC, AND IT HAS TO BE. The card is released from the cursor the
   * moment the drag ends; without applying the new order locally it would snap
   * back to its old slot and hop forward again when the broadcast lands, one
   * round trip later. That flicker is exactly what the drag was built to avoid.
   *
   * Rolls back per this codebase's convention (CLAUDE.md): the previous order is
   * kept, and a rejection — `invalid_reorder`, or `wigs_locked` past the
   * terminal lock — restores it rather than leaving the two people in the
   * session looking at different rows.
   */
  reorderSectionItems: (sectionKey, orderedIds) => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket || !sectionKey || !Array.isArray(orderedIds)) return;
    if (!orderedIds.length) return;

    const snapshot = get().snapshot;
    const section = (snapshot?.sections || []).find(
      (candidate) => candidate.section_key === sectionKey,
    );
    // The order to go back to if the server refuses.
    const previous = (section?.items || []).map(
      (item) => item.execution_blueprint_item_id,
    );

    set({
      lastError: null,
      reorderRollback: previous.length ? { sectionKey, order: previous } : null,
      snapshot: reduceSectionItemsReordered(snapshot, {
        section_key: sectionKey,
        item_order: orderedIds,
      }),
    });

    socket.emit(LIVE_REVIEW_EMIT.REORDER_SECTION_ITEMS, {
      section_key: sectionKey,
      // Numbers on the wire: the server validates these against its own ids,
      // and `itemKey` stringifies for local comparison only.
      ordered_item_ids: orderedIds.map((id) => Number(id)),
    });
  },

  deleteConnector: (connectorId) => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket || connectorId == null) return;
    if (
      get().snapshot?.live_review_step !== LIVE_REVIEW_STEP.CONNECTORS_REVIEW
    ) {
      return;
    }
    set({ connectorPending: true, lastError: null });
    socket.emit(LIVE_REVIEW_EMIT.DELETE_CONNECTOR, {
      execution_blueprint_item_connector_id: connectorId,
    });
  },

  // ── Step 3 (§6b) ──────────────────────────────────────────────────────────

  // ⚠️ NO completeness gate, deliberately. §6b.1: "don't require any minimum
  // number of connectors before enabling the button", and submitting the AI
  // draft untouched is explicitly fine. The only precondition is the step.
  confirmConnections: () => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket) return;
    if (
      get().snapshot?.live_review_step !== LIVE_REVIEW_STEP.CONNECTORS_REVIEW
    ) {
      return;
    }
    set({ lastError: null, advancing: true });
    socket.emit(LIVE_REVIEW_EMIT.CONFIRM_CONNECTIONS);
  },

  setItemShortlist: (itemId, isShortlisted) => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket) return;
    const key = itemKey(itemId);
    if (!key) return;
    if (!_canEditYear1(get().snapshot)) return;

    get()._setItemPending(key, true);
    socket.emit(LIVE_REVIEW_EMIT.SET_ITEM_SHORTLIST, {
      execution_blueprint_item_id: itemId,
      is_year1_shortlisted: !!isShortlisted,
    });
  },

  // `ownerUserId: null` clears the owner — a legitimate value, so this must not
  // guard on truthiness.
  // ⚠️ THE ONE OPTIMISTIC ACTION IN THIS STORE, and the exception is deliberate.
  //
  // Everything else here renders from the server's echo, because in a live
  // meeting two people are watching the same tree and the echo is what keeps
  // them honest. Owner selection is different in the way that matters: it is a
  // dropdown, and a dropdown that closes and then shows the OLD name for a
  // round-trip reads as a click that didn't register. So the pick lands
  // locally first, on instruction ("direct reflect it there not wait till it
  // does event call").
  //
  // Three things make that safe rather than a licence to go optimistic
  // everywhere:
  //
  //   1. **The echo still reconciles.** `item_owner_updated` runs
  //      `reduceOwnerUpdated` exactly as before and overwrites whatever we
  //      guessed — including `owner_assigned_at`, which only the server knows.
  //      The local write is a placeholder, never the final value.
  //   2. **It rolls back.** The previous owner is kept in `ownerRollback` and
  //      restored if the server rejects the change (`invalid_owner`,
  //      `blueprint_already_locked`). Restoring the PREVIOUS value, not
  //      clearing it — same rule as Strategy Canvas's `setBlockStatus`.
  //   3. **It sets no pending flag.** The updated name IS the feedback; a
  //      spinner that disabled the row would undo the responsiveness this
  //      exists for, and stop a coach correcting a misclick immediately.
  //
  // `owner_suggested_by_agent` is forced false: §6b.6 says any deliberate pick
  // counts as a human decision, even re-picking the agent's own suggestion, so
  // the AI badge has to go the moment the row is touched rather than a
  // round-trip later.
  /**
   * @param itemId
   * @param owners  The item's FULL new owner list, not a delta — either an
   *                array of `{ user_id, name }`, an array of ids, or a single
   *                id/null for the one-owner call sites that predate #25.
   *                `[]` or `null` unassigns everyone.
   * @param ownerName  Legacy third argument, kept so the existing single-owner
   *                call sites compile unchanged: `setItemOwner(id, userId, name)`
   *                still means what it always did.
   *
   * ⚠️ BREAKING WIRE CHANGE (handoff #25 §4, 2026-09-14). The emit now sends
   * `owner_user_ids: number[]` instead of `owner_user_id: number | null`. The
   * two shapes are NOT compatible — an old server receiving this drops the
   * owner silently, so this needs the coordinated deploy the handoff calls for.
   *
   * Every id is validated against the company roster server-side and the whole
   * call is rejected with `invalid_owner` if any is unknown: no partial write,
   * which is why the optimistic write below can set the list wholesale.
   */
  setItemOwner: (itemId, owners, ownerName) => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket) return;
    const key = itemKey(itemId);
    if (!key) return;
    if (!_canEditYear1(get().snapshot)) return;

    // Accepts all three shapes the call sites use. Kept here rather than asking
    // every caller to normalise, because the Year 1 screen picks one owner today
    // and will pick several later — and that change should not have to touch
    // this store again.
    const list = Array.isArray(owners)
      ? owners
      : owners == null
        ? []
        : [{ user_id: owners, name: ownerName ?? null }];

    const normalised = list
      .map((entry) =>
        entry && typeof entry === "object"
          ? { user_id: entry.user_id, name: firstDefined(entry.name) }
          : { user_id: entry, name: null },
      )
      .filter((entry) => entry.user_id != null);

    const previous = findItem(get().snapshot, itemId);

    set((state) => ({
      // ⚠️ `?? state.snapshot` is load-bearing: `patchItem` returns NULL when
      // the id isn't in our tree, and assigning that would blank the whole
      // screen. A stale id here is harmless — the emit still goes, and the
      // server's answer (echo or `item_not_found` → resnapshot) settles it.
      snapshot:
        patchItem(state.snapshot, itemId, {
          ...normalizeOwners({
            owner_user_ids: normalised.map((entry) => entry.user_id),
            owners: normalised,
          }),
          owner_suggested_by_agent: false,
        }) ?? state.snapshot,
      // Only the FIRST pick is remembered per item: if a coach picks twice
      // before either echo lands, rolling back to the intermediate value would
      // restore something the server never had.
      ownerRollback: {
        [key]: {
          ...normalizeOwners(previous || {}),
          owner_suggested_by_agent: previous?.owner_suggested_by_agent === true,
        },
        ...state.ownerRollback,
      },
    }));

    const sentIds = normalised.map((entry) => Number(entry.user_id));

    // Recorded BEFORE the emit, so an echo that somehow beats this line still
    // finds a fingerprint to compare against.
    set((state) => ({
      ownerInFlight: {
        ...state.ownerInFlight,
        [key]: ownerFingerprint(sentIds),
      },
    }));

    socket.emit(LIVE_REVIEW_EMIT.SET_ITEM_OWNER, {
      execution_blueprint_item_id: itemId,
      owner_user_ids: sentIds,
    });
  },

  // Undo every optimistic owner write still waiting on an echo.
  //
  // ⚠️ It rolls back ALL of them, not the one that failed, because the error
  // payload isn't documented to carry an item id — the same limitation that
  // makes `_handleServerError` clear every pending flag. In practice there is
  // one in flight; picking owners faster than the socket round-trips is not a
  // real scenario, and over-restoring is the safe direction: the next echo or
  // resnapshot corrects it, whereas leaving a rejected value on screen tells
  // both parties an owner is assigned who isn't.
  _rollbackOwners: () => {
    const pending = get().ownerRollback;
    if (!pending || Object.keys(pending).length === 0) return;
    set((state) => {
      let snapshot = state.snapshot;
      Object.keys(pending).forEach((key) => {
        // Same null guard as the optimistic write above.
        snapshot = patchItem(snapshot, key, pending[key]) ?? snapshot;
      });
      // `ownerInFlight` goes with it: the writes those fingerprints belonged to
      // did not land, so leaving them would make every later echo for those
      // items look stale and be dropped — the cell would stop updating at all.
      return { snapshot, ownerRollback: {}, ownerInFlight: {} };
    });
  },

  /** Puts a rejected optimistic reorder back the way it was. */
  _rollbackReorder: () => {
    const pending = get().reorderRollback;
    if (!pending) return;
    set((state) => ({
      snapshot: reduceSectionItemsReordered(state.snapshot, {
        section_key: pending.sectionKey,
        item_order: pending.order,
      }),
      reorderRollback: null,
    }));
  },

  // ⚠️ THE TERMINAL LOCK. No undo, and §6b.7 says there is no server-side gate —
  // the caller's confirmation dialog is the only thing between a stray click and
  // a permanently locked blueprint. Like `submitFinalize`, the success path is
  // the broadcast (`blueprint_locked`), not a reply, so nothing local is set
  // beyond the in-flight flag.
  confirmYear1Plan: () => {
    const socket = unifiedProjectSocketManager.getSocket(LIVE_REVIEW_NAMESPACE);
    if (!socket) return;
    if (!_canEditYear1(get().snapshot)) return;
    set({ lastError: null, locking: true });
    socket.emit(LIVE_REVIEW_EMIT.CONFIRM_YEAR1_PLAN);
  },

  // ── Internals ─────────────────────────────────────────────────────────────
  _setItemPending: (key, pending) =>
    set((state) => {
      const next = { ...state.pendingItems };
      if (pending) next[key] = true;
      else delete next[key];
      return { pendingItems: next };
    }),

  _handleServerError: (payload) => {
    const code = firstDefined(payload?.code, payload?.error?.code);

    // Before anything else: put any optimistic owner pick back. `invalid_owner`
    // and `blueprint_already_locked` are the documented rejections, but this is
    // unconditional on purpose — an error means the write we guessed at did not
    // happen, whatever its code.
    get()._rollbackOwners();
    // Same reasoning for an optimistic reorder. `invalid_reorder` is the
    // documented rejection and `wigs_locked` the one past the terminal lock,
    // but any error means our guess didn't land — and a row left in an order
    // the server rejected is the two people in the session reading different
    // maps, which is the one failure this screen must not have.
    get()._rollbackReorder();
    const message =
      LIVE_REVIEW_ERROR_COPY[code] ||
      firstDefined(payload?.message, payload?.error?.message) ||
      "Something went wrong in the live review.";

    // Any in-flight row spinner is now wrong — we don't know which item failed
    // (the error payload isn't documented to carry one), and leaving them set
    // would strand every pending row. Same for the connector flag and the lock:
    // an error is the end of whatever was in flight.
    set({
      pendingItems: {},
      connectorPending: false,
      locking: false,
      advancing: false,
      // An add that failed leaves the composer open with the text still in it,
      // which is the right place for it to be — the CEO can fix the wording and
      // resubmit rather than retyping. Only the "working" state clears.
      addPending: null,
    });

    // `not_yet_available` is the expected answer to Continue, not a failure.
    // §7: show it as "you're all set, the next step isn't live yet".
    if (code === LIVE_REVIEW_ERROR_CODE.NOT_YET_AVAILABLE) {
      set({ continueState: { code, message, at: Date.now() } });
      return;
    }

    // `remark_not_found` is silent — it resnapshots (below) but never reaches
    // the user as an error.
    //
    // Marking a remark discussed is ONE-WAY, so "that remark isn't there" and
    // "that remark is already discussed" are the same outcome from the user's
    // point of view: the point is settled. And this code is now *expected* in
    // normal operation — finalising a block emits an accept plus a
    // mark-discussed per open remark, and if the server's own accept happens to
    // resolve one of them first, our follow-up necessarily arrives too late for
    // a remark that no longer exists. Surfacing that as "Something went wrong"
    // would put a red banner on the screen every time the thing the user asked
    // for worked.
    //
    // Deliberately narrow: only this one code. Everything else still shows,
    // including `item_not_found`, which is genuinely wrong — it means we are
    // editing something the blueprint no longer has.
    if (code === LIVE_REVIEW_ERROR_CODE.REMARK_NOT_FOUND) {
      set((state) => ({ resnapshotRequests: state.resnapshotRequests + 1 }));
      return;
    }

    set({ lastError: { code, message, at: Date.now() } });

    // §8 asks for a resnapshot on these: our tree disagrees with the server's,
    // and every further action against it would fail the same way.
    //
    // `wigs_locked` and `wrong_step` are in this list for the same reason even
    // though §8 words them as "shouldn't happen" / "you're likely on
    // connectors_review already": both mean our `live_review_step` is behind the
    // server's, and since the step is what ROUTES the screen, being behind on it
    // is the one piece of staleness that leaves the user looking at the wrong
    // screen entirely. Re-joining is what corrects it (§6a.4).
    //
    // `invalid_connector` and `duplicate_connector` are deliberately NOT here —
    // they are verdicts on what the user just drew, not evidence that our copy
    // is stale, and §8 says not to retry them as-is.
    if (
      code === LIVE_REVIEW_ERROR_CODE.ITEM_NOT_FOUND ||
      // REMARK_NOT_FOUND is handled above — it resnapshots and returns early,
      // silently, so it is deliberately absent from this list rather than
      // missing from it.
      code === LIVE_REVIEW_ERROR_CODE.CONNECTOR_NOT_FOUND ||
      code === LIVE_REVIEW_ERROR_CODE.WIGS_LOCKED ||
      code === LIVE_REVIEW_ERROR_CODE.WRONG_STEP ||
      // Same reasoning as `wigs_locked`: our copy is behind the server's, and
      // since the lock decides whether anything is editable, being behind on it
      // leaves the user looking at controls that no longer work.
      code === LIVE_REVIEW_ERROR_CODE.BLUEPRINT_ALREADY_LOCKED
    ) {
      set((state) => ({ resnapshotRequests: state.resnapshotRequests + 1 }));
    }
  },

  clearError: () => set({ lastError: null }),
  clearContinueState: () => set({ continueState: null }),
}));

// A delta that couldn't be applied because we don't hold the item means our
// tree is stale rather than the event being bad — ask for a fresh snapshot
// instead of dropping it silently. `patchItem` returns null for that case,
// which is why it's distinguished from "unchanged".
function applyReduced(set, get, nextSnapshot, payload) {
  if (nextSnapshot === null) {
    // eslint-disable-next-line no-console
    console.warn(
      "[LiveReviewSocket] delta for an item we don't hold — resnapshotting",
      payload,
    );
    set((state) => ({ resnapshotRequests: state.resnapshotRequests + 1 }));
    return;
  }
  const targetId = firstDefined(
    payload?.execution_blueprint_item_id,
    payload?.item?.execution_blueprint_item_id,
    payload?.data?.execution_blueprint_item_id,
  );
  set({ snapshot: nextSnapshot });
  if (targetId != null) get()._setItemPending(itemKey(targetId), false);
}

// Reload replays some socket payloads as JSON strings rather than objects —
// this bit the EXW interview cards on the CEO side, where `chat_details`
// re-delivers stored events verbatim. Returns `undefined` for an unparseable
// payload so the caller can bail without confusing it with a legitimate null.
function parsePayload(raw, eventName) {
  if (raw == null) return undefined;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(
      `[LiveReviewSocket] ${eventName} — couldn't parse payload`,
      raw,
    );
    return undefined;
  }
}

// React binding. Holds the selector in a ref and subscribes once with `[]`, so
// an inline selector doesn't resubscribe on every render — the stricter of the
// two hook variants across these repos, and worth having here because a live
// session pushes far more updates than a REST screen.
//
// `Object.is` bail-out: selectors returning a stable reference cause zero
// re-renders. Define selectors at module scope regardless — it's the
// documented convention and it keeps this cheap.
export const useLiveReviewSocket = (selector) => {
  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  const [selected, setSelected] = useState(() =>
    selector(liveReviewSocketStore.getState()),
  );

  useEffect(() => {
    // Sync once on mount, in case the store moved between render and effect.
    const current = selectorRef.current(liveReviewSocketStore.getState());
    setSelected((prev) => (Object.is(prev, current) ? prev : current));

    const unsub = liveReviewSocketStore.subscribe((state) => {
      const next = selectorRef.current(state);
      setSelected((prev) => (Object.is(prev, next) ? prev : next));
    });
    return () => unsub();
    // Subscribe once, deliberately: the selector lives in a ref above, so this
    // effect must not re-run when it changes.
    //
    // ⚠️ Bare disable rather than naming `react-hooks/exhaustive-deps`, because
    // this file is byte-identical across both repos and only ONE of them has the
    // react-hooks plugin registered. Naming a rule ESLint doesn't know about is
    // itself an error, so the named form fails in the CEO app.
    // eslint-disable-next-line
  }, []);

  return selected;
};

export default liveReviewSocketStore;
export { liveReviewSocketStore };
